import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Form,
  Button,
  Modal,
  Badge,
  Spinner,
  Alert,
  InputGroup,
  Pagination,
  Dropdown,
  ButtonGroup,
} from "react-bootstrap";
import {
  Search,
  Filter,
  Eye,
  Calendar,
  Users,
  CreditCard,
  Download,
  FileText,
  Database,
  FileSpreadsheet,
} from "lucide-react";
import { jwtDecode } from "jwt-decode";
import "bootstrap/dist/css/bootstrap.min.css";
import PaymentDetailModal from "./PaymentDetailModal";

const PaymentDashboard = () => {
  const [payments, setPayments] = useState([]);
  const [allPaidAmount, setAllPaidAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("paid");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportDatePreset, setExportDatePreset] = useState("all");
  const [exportDateFrom, setExportDateFrom] = useState("");
  const [exportDateTo, setExportDateTo] = useState("");
  const [pendingExportFormat, setPendingExportFormat] = useState(null);

  // Fetch payments from API
  const API_URL = process.env.REACT_APP_API_URL;
  const fetchPayments = async (page = 1, pageSize = 20) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const normalizedStatus =
        statusFilter === "all"
          ? undefined
          : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1);

      const params = new URLSearchParams();
      params.append("page", String(page));
      params.append("pageSize", String(pageSize));
      if (normalizedStatus) params.append("status", normalizedStatus);
      if (searchTerm.trim()) params.append("search", searchTerm.trim());

      const response = await fetch(
        `${API_URL}/api/payments?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch payments");
      }

      const data = await response.json();
      setPayments(data.items);
      setTotalRows(data.total || 0);
      setTotalPages(
        Math.max(1, Math.ceil((data.total || 0) / (data.pageSize || pageSize))),
      );
      setCurrentPage(data.page);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Compute the total paid amount across all pages (unfiltered),
  // so search/status filters in the table do not break this dashboard number.
  const computeAllPaidAmount = async () => {
    try {
      const token = localStorage.getItem("authToken");

      // Get total count unfiltered.
      const initRes = await fetch(
        `${API_URL}/api/payments?page=1&pageSize=20`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!initRes.ok) return;
      const initData = await initRes.json();

      const totalRows = initData.total || 0;
      if (totalRows === 0) {
        setAllPaidAmount(0);
        return;
      }

      // Try fetching all rows in one request
      let allItems = [];
      try {
        const resAll = await fetch(
          `${API_URL}/api/payments?page=1&pageSize=${totalRows}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (resAll.ok) {
          const dataAll = await resAll.json();
          // If API honored pageSize and returned all rows, use them
          if (
            Array.isArray(dataAll.items) &&
            dataAll.items.length >= totalRows
          ) {
            allItems = dataAll.items;
          } else if (
            Array.isArray(dataAll.items) &&
            dataAll.items.length > 0 &&
            dataAll.items.length < totalRows
          ) {
            // API limited the pageSize; fall back to paginated accumulation
            allItems = await fetchPaginatedAll(totalRows, token);
          } else {
            // Unexpected shape, try paginated
            allItems = await fetchPaginatedAll(totalRows, token);
          }
        } else {
          // failed single-request; try paginated
          allItems = await fetchPaginatedAll(totalRows, token);
        }
      } catch (err) {
        // any error -> fallback to paginated
        allItems = await fetchPaginatedAll(totalRows, token);
      }

      const paidSum = (allItems || [])
        .filter((p) => String(p.Status || "").toLowerCase() === "paid")
        .reduce((sum, p) => sum + Number(p.TotalAmount || 0), 0);

      setAllPaidAmount(paidSum);
    } catch (err) {
      // on failure, don't break the UI; leave previous value
      console.error("Failed to compute all paid amount:", err);
    }
  };

  // Helper to fetch all pages sequentially when API limits pageSize
  const fetchPaginatedAll = async (totalRows, token) => {
    const pageSize = 100; // reasonable chunk
    const pages = Math.ceil(totalRows / pageSize);
    let acc = [];
    for (let p = 1; p <= pages; p++) {
      try {
        const r = await fetch(
          `${API_URL}/api/payments?page=${p}&pageSize=${pageSize}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (!r.ok) break;
        const d = await r.json();
        if (Array.isArray(d.items)) acc = acc.concat(d.items);
        // if we somehow collected all rows early, break
        if (acc.length >= totalRows) break;
      } catch (err) {
        console.error("Paginated fetch error:", err);
        break;
      }
    }
    return acc;
  };

  useEffect(() => {
    computeAllPaidAmount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // `fetchPayments` is intentionally recreated; we only want to re-run
  // when pagination/search/filter inputs change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchPayments(currentPage);
  }, [currentPage, searchTerm, statusFilter]);

  // `payments` is already filtered server-side using { searchTerm, statusFilter }.
  const filteredPayments = payments;

  // Format currency
  const formatCurrency = (amount, currency = "LKR") => {
    return `${currency} ${amount.toLocaleString()}`;
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get status badge variant
  const getStatusVariant = (status) => {
    switch (status.toLowerCase()) {
      case "paid":
        return "success";
      case "pending":
        return "warning";
      case "failed":
        return "danger";
      default:
        return "secondary";
    }
  };

  // Calculate dashboard stats
  const dashboardStats = {
    totalPayments: filteredPayments.length,
    totalAmount: filteredPayments
      .filter((p) => p.Status === "Paid")
      .reduce((sum, p) => sum + p.TotalAmount, 0),
    paidAmount: filteredPayments
      .filter((p) => p.Status === "Paid")
      .reduce((sum, p) => sum + p.TotalAmount, 0),
  };

  // Fetch ALL pages (respecting current search/status filters) for export
  const fetchAllForExport = async (dateFrom = null, dateTo = null) => {
    const token = localStorage.getItem("authToken");
    const normalizedStatus =
      statusFilter === "all"
        ? undefined
        : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1);

    const buildParams = (page, pageSize) => {
      const p = new URLSearchParams();
      p.append("page", String(page));
      p.append("pageSize", String(pageSize));
      if (normalizedStatus) p.append("status", normalizedStatus);
      if (searchTerm.trim()) p.append("search", searchTerm.trim());
      if (dateFrom) p.append("from", dateFrom);
      if (dateTo) p.append("to", dateTo);
      return p.toString();
    };

    // Get total count first
    const initRes = await fetch(
      `${API_URL}/api/payments?${buildParams(1, 1)}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (!initRes.ok)
      throw new Error("Failed to fetch payment count for export");
    const initData = await initRes.json();
    const total = initData.total || 0;
    if (total === 0) return [];

    // Try fetching all rows in one request
    try {
      const resAll = await fetch(
        `${API_URL}/api/payments?${buildParams(1, total)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (resAll.ok) {
        const dataAll = await resAll.json();
        if (Array.isArray(dataAll.items) && dataAll.items.length >= total) {
          return dataAll.items;
        }
      }
    } catch (_) {
      // fall through to paginated
    }

    // Fallback: paginate in chunks of 100
    const chunkSize = 100;
    const pages = Math.ceil(total / chunkSize);
    let acc = [];
    for (let p = 1; p <= pages; p++) {
      const r = await fetch(
        `${API_URL}/api/payments?${buildParams(p, chunkSize)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!r.ok) break;
      const d = await r.json();
      if (Array.isArray(d.items)) acc = acc.concat(d.items);
      if (acc.length >= total) break;
    }
    // Client-side date filter as a guarantee (handles APIs that ignore date params)
    if (!dateFrom && !dateTo) return acc;
    return acc.filter((r) => {
      const d = new Date(r.CreatedAt);
      if (dateFrom && d < new Date(dateFrom)) return false;
      if (dateTo && d > new Date(dateTo)) return false;
      return true;
    });
  };

  const openExportModal = (format) => {
    setPendingExportFormat(format);
    setExportDatePreset("all");
    setExportDateFrom("");
    setExportDateTo("");
    setShowExportModal(true);
  };

  const getDateBounds = () => {
    const now = new Date();
    if (exportDatePreset === "24h") {
      const from = new Date(now - 24 * 60 * 60 * 1000);
      return { from: from.toISOString(), to: now.toISOString() };
    }
    if (exportDatePreset === "7d") {
      const from = new Date(now - 7 * 24 * 60 * 60 * 1000);
      return { from: from.toISOString(), to: now.toISOString() };
    }
    if (exportDatePreset === "30d") {
      const from = new Date(now - 30 * 24 * 60 * 60 * 1000);
      return { from: from.toISOString(), to: now.toISOString() };
    }
    if (exportDatePreset === "custom") {
      return {
        from: exportDateFrom ? new Date(exportDateFrom).toISOString() : null,
        to: exportDateTo
          ? new Date(exportDateTo + "T23:59:59").toISOString()
          : null,
      };
    }
    return { from: null, to: null };
  };

  const handleExportConfirm = async () => {
    setShowExportModal(false);
    const { from, to } = getDateBounds();
    if (pendingExportFormat === "csv") await exportToCSV(from, to);
    else if (pendingExportFormat === "json") await exportToJSON(from, to);
    else if (pendingExportFormat === "excel") await exportToExcel(from, to);
    else if (pendingExportFormat === "summary")
      await exportSummaryReport(from, to);
  };

  const handleShowModal = (payment) => {
    setSelectedPayment(payment);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPayment(null);
  };
  const downloadFile = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };
  // ---- Excel export with breakdown (SheetJS) ----
  const exportToExcel = async (dateFrom = null, dateTo = null) => {
    setExportLoading(true);
    try {
      const rows = await fetchAllForExport(dateFrom, dateTo);
      if (rows.length === 0) {
        alert("No data to export.");
        return;
      }

      // Lazy-load to keep the main bundle smaller
      const XLSX = await import("xlsx");

      // 1) Payments sheet rows
      const paymentHeaders = [
        "Id",
        "CustomerCode",
        "CusID",
        "OrderId",
        "Currency",
        "TotalAmount",
        "Status",
        "ReqTransactionUuid",
        "CreatedAt",
        "UpdatedAt",
      ];

      const paymentSheetData = [
        paymentHeaders,
        ...rows.map((p) => [
          p.Id ?? "",
          p.CustomerCode ?? "",
          p.CusID ?? "",
          p.OrderId ?? "",
          p.Currency ?? "",
          p.TotalAmount ?? 0,
          p.Status ?? "",
          p.ReqTransactionUuid ?? "",
          p.CreatedAt ?? "",
          p.UpdatedAt ?? "",
        ]),
      ];

      // 2) Breakdown sheet rows
      // Rule: if Status === 'Paid' and there are FinalAllocations, do not include PendingAllocations for that payment
      const breakdownHeaders = [
        "PaymentId",
        "CustomerCode",
        "OrderId",
        "Type", // Final | Pending
        "AccountCode",
        "Amount",
        "CreatedAt",
      ];

      const breakdownRows = [];
      for (const p of rows) {
        const finals = Array.isArray(p.FinalAllocations)
          ? p.FinalAllocations
          : [];
        const pendings = Array.isArray(p.PendingAllocations)
          ? p.PendingAllocations
          : [];

        const includePending = !(
          String(p.Status || "").toLowerCase() === "paid" && finals.length > 0
        );

        // Final allocations
        for (const a of finals) {
          breakdownRows.push([
            p.Id ?? "",
            p.CustomerCode ?? "",
            p.OrderId ?? "",
            "Paid",
            a.AccountCode ?? "",
            a.Amount ?? 0,
            a.CreatedAt ?? "",
          ]);
        }

        // Pending allocations (conditionally)
        if (includePending) {
          for (const a of pendings) {
            breakdownRows.push([
              p.Id ?? "",
              p.CustomerCode ?? "",
              p.OrderId ?? "",
              "Pending",
              a.AccountCode ?? "",
              a.Amount ?? 0,
              a.CreatedAt ?? "",
            ]);
          }
        }
      }

      const breakdownSheetData = [breakdownHeaders, ...breakdownRows];

      // Build workbook
      const wb = XLSX.utils.book_new();
      const wsPayments = XLSX.utils.aoa_to_sheet(paymentSheetData);
      const wsBreakdown = XLSX.utils.aoa_to_sheet(breakdownSheetData);

      XLSX.utils.book_append_sheet(wb, wsPayments, "Payments");
      XLSX.utils.book_append_sheet(wb, wsBreakdown, "Breakdown");

      // File name
      const filename = `payments_${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, filename);
    } catch (err) {
      alert("Export failed: " + err.message);
    } finally {
      setExportLoading(false);
    }
  };

  const exportToCSV = async (dateFrom = null, dateTo = null) => {
    setExportLoading(true);
    try {
      const rows = await fetchAllForExport(dateFrom, dateTo);
      if (!rows || rows.length === 0) {
        alert("No data to export.");
        return;
      }

      // Pick columns you want in CSV
      const headers = [
        "CustomerCode",
        "CusID",
        "OrderId",
        "Currency",
        "TotalAmount",
        "Status",
        "ReqTransactionUuid",
        "CreatedAt",
        "UpdatedAt",
      ];

      const escape = (val) => {
        if (val == null) return "";
        const s = String(val);
        // Quote if contains comma, quote or newline
        if (/[",\n]/.test(s)) {
          return `"${s.replace(/"/g, '""')}"`;
        }
        return s;
      };

      const lines = [
        headers.join(","),
        ...rows.map((r) => headers.map((h) => escape(r[h])).join(",")),
      ];

      const csv = lines.join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      downloadFile(
        blob,
        `payments_${new Date().toISOString().slice(0, 10)}.csv`,
      );
    } catch (err) {
      alert("Export failed: " + err.message);
    } finally {
      setExportLoading(false);
    }
  };

  const exportToJSON = async (dateFrom = null, dateTo = null) => {
    setExportLoading(true);
    try {
      const rows = await fetchAllForExport(dateFrom, dateTo);
      const blob = new Blob([JSON.stringify(rows, null, 2)], {
        type: "application/json",
      });
      downloadFile(
        blob,
        `payments_${new Date().toISOString().slice(0, 10)}.json`,
      );
    } catch (err) {
      alert("Export failed: " + err.message);
    } finally {
      setExportLoading(false);
    }
  };

  const exportSummaryReport = async (dateFrom = null, dateTo = null) => {
    setExportLoading(true);
    try {
      const rows = await fetchAllForExport(dateFrom, dateTo);

      const totals = rows.reduce(
        (acc, p) => {
          const status = (p.Status || "Unknown").toLowerCase();
          acc.totalCount += 1;
          acc.totalAmount += Number(p.TotalAmount || 0);
          acc.byStatus[status] = acc.byStatus[status] || {
            count: 0,
            amount: 0,
          };
          acc.byStatus[status].count += 1;
          acc.byStatus[status].amount += Number(p.TotalAmount || 0);
          return acc;
        },
        { totalCount: 0, totalAmount: 0, byStatus: {} },
      );

      // Simple CSV summary
      const lines = [
        "Metric,Value",
        `Total Rows,${totals.totalCount}`,
        `Total Amount,${totals.totalAmount}`,
        "",
        "Status,Count,Amount",
        ...Object.entries(totals.byStatus).map(
          ([s, x]) => `${s},${x.count},${x.amount}`,
        ),
      ];

      const blob = new Blob([lines.join("\n")], {
        type: "text/csv;charset=utf-8;",
      });
      downloadFile(
        blob,
        `payments_summary_${new Date().toISOString().slice(0, 10)}.csv`,
      );
    } catch (err) {
      alert("Export failed: " + err.message);
    } finally {
      setExportLoading(false);
    }
  };
  // Generate pagination items
  const renderPaginationItems = () => {
    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => setCurrentPage(page)}
        >
          {page}
        </Pagination.Item>,
      );
    }

    return items;
  };

  if (error) {
    return (
      <Container
        fluid
        className="min-vh-100 d-flex align-items-center justify-content-center bg-light"
      >
        <div className="text-center">
          <Alert variant="danger">
            <Alert.Heading>⚠️ Error</Alert.Heading>
            <p>{error}</p>
            <Button
              variant="primary"
              onClick={() => fetchPayments(currentPage)}
            >
              Retry
            </Button>
          </Alert>
        </div>
      </Container>
    );
  }
  const authToken = localStorage.getItem("authToken");
  let tokenRole = "";
  try {
    tokenRole = authToken ? jwtDecode(authToken)?.role || "" : "";
  } catch (e) {
    tokenRole = "";
  }
  const userRole = (
    localStorage.getItem("userRole") ||
    tokenRole ||
    ""
  ).toLowerCase();
  const isAdmin = userRole === "admin";

  return (
    <div className="min-vh-100 bg-light">
      {/* Header */}
      <div className="bg-white shadow-sm border-bottom">
        <Container fluid>
          <Row className="py-4">
            <Col>
              <h1 className="display-5 fw-bold text-dark mb-2">
                Payment Dashboard
              </h1>
              <p className="text-muted">Manage and monitor customer payments</p>
            </Col>
          </Row>
        </Container>
      </div>

      <Container fluid className="py-4">
        {/* Dashboard Stats */}
        <Row className="mb-4">
          <Col xl={3} lg={6} mb={3}>
            <Card className="h-100">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <CreditCard className="text-primary me-3" size={32} />
                  <div>
                    <div className="text-muted small">Total Payments</div>
                    <div className="h4 fw-bold mb-0">
                      {dashboardStats.totalPayments}
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xl={3} lg={6} mb={3}>
            <Card className="h-100">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <Users className="text-success me-3" size={32} />
                  <div>
                    {isAdmin && (
                      <>
                        <div className="text-muted small">
                          Paid Amount (All pages)
                        </div>
                        <div className="h4 fw-bold mb-0 text-success">
                          {formatCurrency(allPaidAmount)}
                        </div>
                      </>
                    )}
                    {!isAdmin && (
                      <div className="text-muted small">Amount hidden</div>
                    )}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card className="mb-4">
          <Card.Body>
            <Row className="align-items-end">
              <Col md={6} className="mb-2 mb-md-0">
                <Form.Label>Search Payments</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <Search size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search by Customer Code, Order ID, or Transaction UUID..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </InputGroup>
              </Col>

              <Col md={3} className="mb-2 mb-md-0">
                <Form.Label>Filter by Status</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <Filter size={16} />
                  </InputGroup.Text>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="all">All Status</option>
                    <option value="paid">Paid</option>
                    <option value="pending">Pending</option>
                    <option value="failed">Failed</option>
                  </Form.Select>
                </InputGroup>
              </Col>

              <Col md={3}>
                <Form.Label>Export Data</Form.Label>
                <Dropdown as={ButtonGroup} className="w-100">
                  <Button
                    variant="success"
                    onClick={() => openExportModal("csv")}
                    disabled={exportLoading}
                    className="d-flex align-items-center justify-content-center"
                  >
                    {exportLoading ? (
                      <Spinner animation="border" size="sm" className="me-2" />
                    ) : (
                      <Download size={16} className="me-2" />
                    )}
                    {exportLoading ? "Exporting..." : "Export"}
                  </Button>

                  <Dropdown.Toggle
                    split
                    variant="success"
                    disabled={exportLoading}
                    id="dropdown-split-basic"
                  />

                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => openExportModal("csv")}>
                      <FileText size={16} className="me-2" />
                      Export as CSV
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => openExportModal("json")}>
                      <Database size={16} className="me-2" />
                      Export as JSON
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={() => openExportModal("summary")}>
                      <FileText size={16} className="me-2" />
                      Summary Report
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={() => openExportModal("excel")}>
                      <FileSpreadsheet size={16} className="me-2" />
                      Export to Excel (.xlsx) with Breakdown
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Col>
            </Row>

            {(searchTerm.trim() || statusFilter !== "all") && (
              <Row className="mt-3">
                <Col>
                  <Alert variant="info" className="mb-0 py-2">
                    <small>
                      <strong>Note:</strong> Export will include{" "}
                      {filteredPayments.length} rows currently shown for the
                      applied search/status filters.
                    </small>
                  </Alert>
                </Col>
              </Row>
            )}
          </Card.Body>
        </Card>

        {/* Payments Table */}
        <Card>
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-5">
                <Spinner
                  animation="border"
                  variant="primary"
                  className="mb-3"
                />
                <div className="text-muted">Loading payments...</div>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="border-0 text-uppercase small fw-bold text-muted py-3">
                          Customer
                        </th>
                        <th className="border-0 text-uppercase small fw-bold text-muted py-3">
                          Order ID
                        </th>
                        <th className="border-0 text-uppercase small fw-bold text-muted py-3">
                          Amount
                        </th>
                        <th className="border-0 text-uppercase small fw-bold text-muted py-3">
                          Status
                        </th>
                        <th className="border-0 text-uppercase small fw-bold text-muted py-3">
                          Date
                        </th>
                        <th className="border-0 text-uppercase small fw-bold text-muted py-3">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPayments.map((payment) => (
                        <tr key={payment.Id}>
                          <td className="py-3">
                            <div className="fw-bold">
                              {payment.CustomerCode}
                            </div>
                            <small className="text-muted">
                              ID: {payment.CusID}
                            </small>
                          </td>
                          <td className="py-3">
                            <code className="small">
                              {payment.OrderId.substring(0, 25)}...
                            </code>
                          </td>
                          <td className="py-3">
                            <span className="fw-bold">
                              {formatCurrency(
                                payment.TotalAmount,
                                payment.Currency,
                              )}
                            </span>
                          </td>
                          <td className="py-3">
                            <Badge bg={getStatusVariant(payment.Status)}>
                              {payment.Status}
                            </Badge>
                          </td>
                          <td className="py-3">
                            <small className="text-muted">
                              {formatDate(payment.CreatedAt)}
                            </small>
                          </td>
                          <td className="py-3">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleShowModal(payment)}
                              className="d-flex align-items-center"
                            >
                              <Eye size={14} className="me-1" />
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {/* Pagination */}
                <div className="d-flex justify-content-between align-items-center p-3 bg-light border-top">
                  <div className="text-muted small">
                    Showing {filteredPayments.length} of {totalRows} results
                  </div>
                  <Pagination className="mb-0">
                    <Pagination.First
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(1)}
                    />
                    <Pagination.Prev
                      disabled={currentPage === 1}
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                    />
                    {renderPaginationItems()}
                    <Pagination.Next
                      disabled={currentPage === totalPages}
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                      }
                    />
                    <Pagination.Last
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(totalPages)}
                    />
                  </Pagination>
                </div>
              </>
            )}
          </Card.Body>
        </Card>
      </Container>

      <PaymentDetailModal
        show={showModal}
        onHide={handleCloseModal}
        payment={selectedPayment}
        isAdmin={isAdmin}
        formatCurrency={formatCurrency}
        formatDate={formatDate}
        getStatusVariant={getStatusVariant}
      />

      {/* Export Date Range Modal */}
      <Modal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <Calendar size={18} className="me-2" />
            Export Date Range
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Label className="fw-bold mb-2 d-block">Quick Select</Form.Label>
          <div className="d-flex gap-2 flex-wrap mb-3">
            {[
              { value: "all", label: "All Time" },
              { value: "24h", label: "Last 24 Hours" },
              { value: "7d", label: "Last 7 Days" },
              { value: "30d", label: "Last 30 Days" },
              { value: "custom", label: "Custom Range" },
            ].map((opt) => (
              <Button
                key={opt.value}
                size="sm"
                variant={
                  exportDatePreset === opt.value
                    ? "primary"
                    : "outline-secondary"
                }
                onClick={() => setExportDatePreset(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </div>

          {exportDatePreset === "custom" && (
            <Row>
              <Col>
                <Form.Label>From</Form.Label>
                <Form.Control
                  type="date"
                  value={exportDateFrom}
                  onChange={(e) => setExportDateFrom(e.target.value)}
                />
              </Col>
              <Col>
                <Form.Label>To</Form.Label>
                <Form.Control
                  type="date"
                  value={exportDateTo}
                  onChange={(e) => setExportDateTo(e.target.value)}
                />
              </Col>
            </Row>
          )}

          {exportDatePreset !== "all" && exportDatePreset !== "custom" && (
            <Alert variant="info" className="mb-0 mt-2 py-2">
              <small>
                Will export data from{" "}
                <strong>
                  {exportDatePreset === "24h" && "the last 24 hours"}
                  {exportDatePreset === "7d" && "the last 7 days"}
                  {exportDatePreset === "30d" && "the last 30 days"}
                </strong>
                .
              </small>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowExportModal(false)}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleExportConfirm}
            disabled={exportLoading}
          >
            {exportLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Exporting...
              </>
            ) : (
              <>
                <Download size={16} className="me-2" />
                Export
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PaymentDashboard;
